import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  ArrowLeft, Send, Loader2, Rocket, Sparkles, History, RotateCcw, FlaskConical,
  Gamepad2, Wand2, CheckCircle2, Clock, Star, Volume2, Boxes, Share2, Users, Layers,
  Film, LayoutDashboard, MoreHorizontal, Undo2, Redo2, Eye, Palette, Zap,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSpeech from "@/lib/useSpeech";
import GameCanvas from "@/components/GameCanvas";
import LiveCreatorBar from "@/components/LiveCreatorBar";
import AutoPlaytestButton from "@/components/AutoPlaytestButton";
import TestPlay from "@/components/TestPlay";
import SaveFiles from "@/components/SaveFiles";
import ShareExportDialog from "@/components/ShareExportDialog";
import CollaboratorsDialog from "@/components/CollaboratorsDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BuilderPanels } from "@/components/builder/BuilderPanels";
import { useAuth } from "@/context/AuthContext";
import MakeItAmazingDialog from "@/components/MakeItAmazingDialog";
import WorkforcePulse from "@/components/WorkforcePulse";
import BuilderWelcome from "@/components/BuilderWelcome";
import { Mic, MicOff } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { QuickFXTray } from "@/components/QuickFXTray";
import { Map as MapIcon } from "lucide-react";
import { Globe2 } from "lucide-react";
import { Clapperboard } from "lucide-react";
import { CloudSun } from "lucide-react";
import { Bug } from "lucide-react";
import { FileCode2 } from "lucide-react";
import { BrainCircuit } from "lucide-react";
import { toast } from "sonner";

const QUICK = ["Make the player faster", "Add more collectibles", "Make it easier", "Change the background color", "Add more enemies"];
const NPC_VOICES = ["fable", "nova", "shimmer", "coral", "sage", "echo"];
const npcVoice = (i) => NPC_VOICES[i % NPC_VOICES.length];
const CATEGORIES = ["Platformer", "Racing", "Puzzle", "Shooter", "Adventure", "Quiz", "Educational", "Card", "Reaction", "Funny", "Kids", "School Projects"];

// Detect a big, multi-step chat prompt worth handing off to the AI Game Director (Autopilot).
const MULTISTEP_RE = /\b(then|also|next|afterwards|and then|after ?that)\b/i;
const LIST_RE = /(^|\n)\s*(\d+[.)]|[-*•])\s+\S/;
const isComplexPrompt = (t) => {
  const s = (t || "").trim();
  return s.length > 150 || MULTISTEP_RE.test(s) || LIST_RE.test(s);
};

export default function GameBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = (user && typeof user === "object" && user.role === "superadmin") || false;
  const [game, setGame] = useState(null);
  const [config, setConfig] = useState(null);
  const [livePreview, setLivePreview] = useState(null);   // Live Creator sandbox (un-saved preview)
  const [activeVersion, setActiveVersion] = useState(null);
  // Reset the Undo History "Now" marker to the latest whenever a new version is pushed.
  useEffect(() => { setActiveVersion(null); }, [game?.versions?.length]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [testPlayOpen, setTestPlayOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [pubForm, setPubForm] = useState({ description: "", category: "Adventure" });
  const [playtest, setPlaytest] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [artPrompt, setArtPrompt] = useState("");
  const [artLoading, setArtLoading] = useState("");
  const [libOpen, setLibOpen] = useState(false);
  const [libChars, setLibChars] = useState([]);
  const [libTarget, setLibTarget] = useState("player");
  const [worldPrompt, setWorldPrompt] = useState("");
  const [worldLoading, setWorldLoading] = useState(false);
  const histRef = useRef({ stack: [], idx: -1, applying: false });
  const [histState, setHistState] = useState({ idx: -1, len: 0 });
  const [savesRefresh, setSavesRefresh] = useState(0);
  const [comparePreview, setComparePreview] = useState(null);
  const [compareIdx, setCompareIdx] = useState(null);
  const [autopilotHint, setAutopilotHint] = useState(null);
  const [outlineSteps, setOutlineSteps] = useState(null);
  const [outlineBusy, setOutlineBusy] = useState(false);
  const [fxOpen, setFxOpen] = useState(false);
  const [amazingOpen, setAmazingOpen] = useState(false);
  const [amazingTarget, setAmazingTarget] = useState("game");
  const [pendingEdit, setPendingEdit] = useState("");
  const [visualQa, setVisualQa] = useState(null);
  const [visionBusy, setVisionBusy] = useState(false);
  const [autofixBusy, setAutofixBusy] = useState(false);
  const [uniqueness, setUniqueness] = useState(null);
  const lastVisionScore = useRef(null);
  useEffect(() => {
    function onMsg(e) {
      const d = e && e.data;
      if (!d || d.type !== "gv-visualqa" || !d.report) return;
      setVisualQa({ ...d.report, thumb: d.thumb });
      if (id) api.post(`/creator/${id}/visualqa`, {
        score: d.report.score, demoLook: d.report.demoLook,
        issues: d.report.issues || [], stats: d.report.stats || {},
      }).catch(() => {});
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [id]);

  // AI-vision pass — GPT-5 vision inspects a live screenshot for subtle art defects.
  const runVisionQa = async (thumb, auto = false) => {
    const img = thumb || visualQa?.thumb;
    if (!id || !img) { if (!auto) toast.error("Load the preview first, then run AI Vision QA."); return; }
    setVisionBusy(true);
    try {
      const r = await api.post(`/creator/${id}/visualqa/vision`, {
        thumb: img, genre: config?.template, template: config?.template,
      });
      const ai = r.data?.ai || null;
      setVisualQa((v) => ({ ...(v || {}), ai }));
      const n = (ai?.defects || []).length;
      toast.success(n ? `AI Vision QA flagged ${n} art issue(s)` : "AI Vision QA: renders clean ✓");
    } catch (e) { if (!auto) toast.error(formatApiError(e)); }   // auto-run failures stay silent (no credit/error spam)
    finally { setVisionBusy(false); }
  };
  // Throttled auto-run: re-scan with AI vision only when the geometry score changes (silent on failure).
  useEffect(() => {
    if (!visualQa || !visualQa.thumb || visualQa.score == null) return;
    if (lastVisionScore.current === visualQa.score) return;
    lastVisionScore.current = visualQa.score;
    runVisionQa(visualQa.thumb, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualQa?.score]);

  // Vision Auto-Fix — feed AI-vision defects into the auto-polish loop (deterministic config patch).
  const runVisualAutofix = async () => {
    if (!id) return;
    setAutofixBusy(true);
    try {
      const r = await api.post(`/creator/${id}/visualqa/autofix`);
      if (r.data?.config) { setConfig(r.data.config); setGame((g) => (g ? { ...g, config: r.data.config } : g)); }
      toast.success(r.data?.summary || "Auto-fixed visuals 🎨");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAutofixBusy(false); }
  };

  // Visual Uniqueness score — how distinct this build looks vs the creator's other games.
  useEffect(() => {
    if (!id) return;
    api.get(`/creator/${id}/uniqueness`).then((r) => setUniqueness(r.data)).catch(() => {});
  }, [id]);

  // Consume a Code Brain / Game Director handoff (instruction stashed in sessionStorage).
  useEffect(() => {
    try {
      const p = sessionStorage.getItem("gv_pending_edit");
      if (p) { setPendingEdit(p); sessionStorage.removeItem("gv_pending_edit"); }
    } catch {}
  }, []);

  // ── Undo Everywhere ──────────────────────────────────────────────────────
  // A reusable one-tap Undo→Redo toast for any change that mutates game.config
  // (Quick FX, AI chat edits, AI art, skybox). Prefers whole-snapshot version
  // revert (clean history + removes added keys like skyboxUrl); falls back to
  // restoring the in-memory config via PATCH when no version is available.
  const configUndo = (prevConfig, prevVersion) => async () => {
    if (prevVersion != null) {
      try {
        const { data } = await api.post(`/games/${id}/revert/${prevVersion}`);
        const cfg = data.config || prevConfig;
        setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g));
        return;
      } catch { /* fall through to in-memory restore */ }
    }
    if (prevConfig) {
      setConfig(prevConfig); setGame((g) => (g ? { ...g, config: prevConfig } : g));
      await persistConfig(prevConfig, "Undo");
    }
  };
  const configRedo = (newConfig, newVersion, summary) => async () => {
    if (newVersion != null) {
      try {
        const { data } = await api.post(`/games/${id}/revert/${newVersion}`);
        const cfg = data.config || newConfig;
        setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g));
        return;
      } catch { /* fall through */ }
    }
    if (newConfig) {
      setConfig(newConfig); setGame((g) => (g ? { ...g, config: newConfig } : g));
      await persistConfig(newConfig, summary || "Redo");
    }
  };

  const showRedoToast = (doRedo, summary) => {
    toast.custom((tid) => (
      <div data-testid="quickfx-redo-toast"
        className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl px-4 py-3 min-w-[260px] pointer-events-auto">
        <RotateCcw className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
        <span className="text-sm font-medium truncate">Undone ↩️</span>
        <button data-testid="quickfx-redo-btn"
          onClick={async () => { toast.dismiss(tid); await doRedo(); toast.success("Redone ↪️"); }}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] text-xs font-semibold px-3 py-1 hover:bg-[hsl(var(--secondary))] shrink-0">
          <Redo2 className="w-3.5 h-3.5" /> Redo
        </button>
      </div>
    ), { duration: 10000, position: "bottom-center" });
  };

  const showUndoToast = (summary, doUndo, doRedo) => {
    toast.custom((tid) => (
      <div data-testid="quickfx-undo-toast"
        className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl px-4 py-3 min-w-[280px] pointer-events-auto">
        <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
        <span className="text-sm font-medium truncate">{summary} ✓</span>
        <button data-testid="quickfx-undo-btn"
          onClick={async () => { toast.dismiss(tid); await doUndo(); if (doRedo) showRedoToast(doRedo, summary); }}
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold px-3 py-1 hover:opacity-90 shrink-0">
          <Undo2 className="w-3.5 h-3.5" /> Undo
        </button>
      </div>
    ), { duration: 6000, position: "bottom-center" });
  };

  const undoableConfigChange = (prevConfig, prevVersion, newConfig, newVersion, summary) =>
    showUndoToast(summary, configUndo(prevConfig, prevVersion), configRedo(newConfig, newVersion, summary));

  const applyQuickFx = (cfg, version, summary) => {
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setConfig(cfg);
    setGame((g) => ({ ...g, config: cfg, versions: [...(g.versions || []), { version, config: cfg, summary, created_at: new Date().toISOString() }] }));
    undoableConfigChange(prevConfig, prevVersion, cfg, version, summary);
  };

  const toggleCompare = (idx, beforeCfg) => {
    if (compareIdx === idx) { setComparePreview(null); setCompareIdx(null); }
    else { setComparePreview(beforeCfg); setCompareIdx(idx); }
  };

  // Silent auto-save checkpoint of the CURRENT (pre-change) state before a big AI action
  const autoCheckpoint = async (label) => {
    try {
      const { data } = await api.post(`/games/${id}/saves`, { name: label, auto: true });
      setSavesRefresh((k) => k + 1);
      return data.id;
    } catch { return null; }
  };

  const revertToCheckpoint = async (sid) => {
    try {
      const { data } = await api.post(`/games/${id}/saves/${sid}/restore`);
      if (data.config) setConfig(data.config);
      setComparePreview(null); setCompareIdx(null);
      toast.success("Reverted to before that change ↩️");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't revert — that checkpoint may be gone"); }
  };

  // Undo/redo history — captures every config change (chat edits, world, skybox, settings, audio, restores)
  useEffect(() => {
    if (!config) return;
    const h = histRef.current;
    if (h.applying) { h.applying = false; return; }
    const snap = JSON.stringify(config);
    if (h.idx >= 0 && JSON.stringify(h.stack[h.idx]) === snap) return;
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push(config);
    if (h.stack.length > 40) h.stack.shift();
    h.idx = h.stack.length - 1;
    setHistState({ idx: h.idx, len: h.stack.length });
  }, [config]);

  const persistConfig = async (cfg, summary) => {
    try { await api.patch(`/games/${id}/config`, { config: cfg, summary }); } catch { /* best-effort */ }
  };
  const undo = () => {
    const h = histRef.current;
    if (h.idx <= 0) return;
    h.idx -= 1; h.applying = true;
    const target = h.stack[h.idx];
    setHistState({ idx: h.idx, len: h.stack.length });
    setConfig(target);
    persistConfig(target, "Undo");
  };
  const redo = () => {
    const h = histRef.current;
    if (h.idx >= h.stack.length - 1) return;
    h.idx += 1; h.applying = true;
    const target = h.stack[h.idx];
    setHistState({ idx: h.idx, len: h.stack.length });
    setConfig(target);
    persistConfig(target, "Redo");
  };
  const [storyLoading, setStoryLoading] = useState(false);
  const [encLoading, setEncLoading] = useState(false);
  const [encWaves, setEncWaves] = useState(8);
  const [questLoading, setQuestLoading] = useState(false);
  const [questCount, setQuestCount] = useState(5);
  const [npcOpen, setNpcOpen] = useState(false);
  const [npcIdx, setNpcIdx] = useState(0);
  const [npcMsgs, setNpcMsgs] = useState([]);
  const [npcInput, setNpcInput] = useState("");
  const [npcSending, setNpcSending] = useState(false);
  const [npcSession, setNpcSession] = useState(null);
  const [speaking, setSpeaking] = useState("");
  const [models, setModels] = useState(null);
  const [modelPolling, setModelPolling] = useState(false);
  const [genCastBusy, setGenCastBusy] = useState(false);
  const [castStyle, setCastStyle] = useState("realistic");
  const [rigBusy, setRigBusy] = useState(false);
  const succeededRef = useRef(0);
  const modelSigRef = useRef("");
  const [artStatus, setArtStatus] = useState(null); // 2D AAA auto-art progress
  const [artPolling, setArtPolling] = useState(false);
  const artDoneRef = useRef(0);
  const [audioPolling, setAudioPolling] = useState(false); // auto-soundtrack progress
  const audioInitRef = useRef(false);
  const [voicePolling, setVoicePolling] = useState(false); // voice-acting generation progress
  const [voiceBusy, setVoiceBusy] = useState(false);
  const canvasRef = useRef(null);
  const { listening, start, stop } = useSpeech((t) => setInput((p) => (p ? p + " " : "") + t));
  const { listening: npcListening, supported: npcMicSupported, start: npcStart, stop: npcStop } = useSpeech((t) => setNpcInput((p) => (p ? p + " " : "") + t));

  const load = async () => {
    try {
      const { data } = await api.get(`/games/${id}`);
      setGame(data);
      setConfig(data.config);
      setCastStyle(data.art_style3d === "sculpture" ? "sculpture" : "realistic");
      setPubForm({ description: data.description || "", category: data.category || "Adventure" });
      setMessages([{ role: "studio", text: `Your game "${data.title}" is ready to play! 🎮 Tell me anything you'd like to change.` }]);
    } catch (err) {
      toast.error("Could not load this game.");
      navigate("/app/games");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Sync edits applied by the floating Studio AI chat (DirectorAssistant) into the live preview.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (!d.config) return;
      setConfig(d.config);
      setGame((g) => (g ? { ...g, config: d.config } : g));
      setMessages((m) => [...m, { role: "studio", text: `✨ ${d.summary}` }]);
      if (d.model_generating) startCastPolling();
      toast.success("Studio AI applied your change ✨");
    };
    window.addEventListener("gv-director-edited", handler);
    const playHandler = (e) => {
      const name = (e.detail || {}).name || "intro";
      try { canvasRef.current?.playCutscene(name); } catch {}
      setMessages((m) => [...m, { role: "studio", text: `🎬 Playing the "${name.replace(/_/g, " ")}" cutscene in your preview…` }]);
    };
    window.addEventListener("gv-director-play-cutscene", playHandler);
    return () => {
      window.removeEventListener("gv-director-edited", handler);
      window.removeEventListener("gv-director-play-cutscene", playHandler);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const chatLogRef = useRef(null);
  useEffect(() => {
    const el = chatLogRef.current;
    if (el && messages.length) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const sendEdit = async (text) => {
    const instruction = (text || input).trim();
    if (!instruction || editing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: instruction }]);
    setAutopilotHint(isComplexPrompt(instruction) ? instruction : null);
    setOutlineSteps(null);
    setEditing(true);
    try {
      const beforeConfig = config;
      const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
      const cpId = await autoCheckpoint(`Before: ${instruction.slice(0, 42)}`);
      const { data } = await api.post(`/games/${id}/edit`, { instruction });
      setConfig(data.config);
      if (!data.answered) {
        setMessages((m) => [...m, { role: "studio", text: data.summary, revertTo: cpId, before: beforeConfig, suggestions: data.suggestions || [] }]);
        setGame((g) => ({ ...g, config: data.config, versions: [...(g.versions || []), { version: data.version, config: data.config, summary: data.summary, created_at: new Date().toISOString() }] }));
        undoableConfigChange(beforeConfig, prevVersion, data.config, data.version, data.summary || "Applied your change");
      } else {
        setMessages((m) => [...m, { role: "studio", text: data.summary }]);
      }
      if (data.model_generating) {
        toast.success("Sculpting your 3D character… it'll pop into the preview in a few minutes. 🧊");
        setMessages((m) => [...m, { role: "studio", text: "🧊 I'm sculpting that as a real 3D model — it'll appear in your game automatically once it's ready (a few minutes)." }]);
        startCastPolling();
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "studio", text: formatApiError(err.response?.data?.detail) }]);
    } finally {
      setEditing(false);
    }
  };

  const outlinePreview = async () => {
    if (outlineBusy || !autopilotHint) return;
    setOutlineBusy(true);
    try {
      const { data } = await api.post(`/games/${id}/autopilot/outline`, { goal: autopilotHint }, { timeout: 45000 });
      setOutlineSteps(data.steps || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't sketch an outline — try the Director.");
    } finally { setOutlineBusy(false); }
  };

  // ---- Meshy 3D character models ----
  const is3D = config?.dimension === "3d";
  const isBase = config?.dimension === "base";
  // Robot playtester supports 3D worlds + the movement-based 2D arcade templates.
  const BOT_2D_TEMPLATES = ["platformer", "runner", "shooter", "maze", "racing"];
  const botSupported = is3D || BOT_2D_TEMPLATES.includes(config?.template);
  const refreshModels = async () => {
    try {
      const { data } = await api.get(`/bridge/models-status/${id}`);
      const list = data.models || [];
      setModels(list);
      // Refetch config whenever a model's GLB url appears OR swaps (static → rigged/animated),
      // so the live preview picks up both the model and its walking animation the moment it's ready.
      const sig = list.map((m) => `${m.asset_id}:${m.glb || ""}`).join("|");
      if (sig !== modelSigRef.current) {
        modelSigRef.current = sig;
        try {
          const { data: g } = await api.get(`/games/${id}`);
          setConfig(g.config);
          setGame((prev) => ({ ...prev, config: g.config }));
        } catch { /* ignore */ }
      }
      return data.all_done;
    } catch { return true; }
  };
  const startCastPolling = () => { setModelPolling(true); refreshModels(); };
  const generate3DCast = async () => {
    if (genCastBusy || modelPolling) return;
    setGenCastBusy(true);
    try {
      const { data } = await api.post(`/bridge/generate-models/${id}`, { art_style: castStyle });
      if (data.count > 0) {
        toast.success(`Sculpting ${data.count} real 3D model${data.count > 1 ? "s" : ""}… this takes a few minutes. 🧊`);
        startCastPolling();
      } else {
        toast.info("Every character already has a 3D model — or there are none to generate yet.");
        refreshModels();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start 3D generation"); }
    finally { setGenCastBusy(false); }
  };
  const rigCast = async () => {
    if (rigBusy || modelPolling) return;
    setRigBusy(true);
    try {
      const { data } = await api.post(`/bridge/rig-cast/${id}`);
      if (data.started > 0) {
        toast.success(`Animating ${data.started} character${data.started > 1 ? "s" : ""}… they'll start walking in a few minutes. 🎞️`);
        startCastPolling();
      } else {
        toast.info("Every character is already animated — or there are no static humanoid models to rig yet.");
        refreshModels();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start animating"); }
    finally { setRigBusy(false); }
  };
  // On load (3D games): check if any models are mid-generation and resume polling.
  useEffect(() => {
    if (!is3D || !game) return;
    (async () => {
      const done = await refreshModels();
      if (!done) setModelPolling(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is3D, id]);
  useEffect(() => {
    if (!modelPolling) return;
    const t = setInterval(async () => {
      const done = await refreshModels();
      if (done) { setModelPolling(false); }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPolling]);

  // ---- 2D AAA auto-art (sprite art for the whole cast) ----
  const refreshArt = async () => {
    try {
      const { data } = await api.get(`/games/${id}/art-status`);
      setArtStatus(data);
      const done = (data.targets || []).filter((t) => t.status === "done").length;
      if (done > artDoneRef.current) {
        artDoneRef.current = done;
        // Merge the freshly-drawn sprites into the live config as they arrive.
        const imgs = data.images || {};
        setConfig((c) => {
          if (!c) return c;
          const next = JSON.parse(JSON.stringify(c));
          if (imgs.player && next.player) next.player.image = imgs.player;
          if (imgs.collectible && next.collectible) next.collectible.image = imgs.collectible;
          if (imgs.enemy && next.enemy) next.enemy.image = imgs.enemy;
          if (imgs.boss && next.boss) next.boss.image = imgs.boss;
          return next;
        });
      }
      if (data.done) {
        // One full sync at the end to pull anything else the AAA pipeline added (quests/story).
        try { const { data: g } = await api.get(`/games/${id}`); if (g.config) { setConfig(g.config); setGame((p) => ({ ...p, config: g.config })); } } catch {}
      }
      return data.done;
    } catch { return true; }
  };
  const artInitRef = useRef(false);
  useEffect(() => {
    if (is3D || !game || artInitRef.current) return;
    artInitRef.current = true;
    (async () => {
      if (!game.art_generating) return;
      const done = await refreshArt();
      if (!done) setArtPolling(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is3D, id, game]);
  useEffect(() => {
    if (!artPolling) return;
    const t = setInterval(async () => {
      const done = await refreshArt();
      if (done) { setArtPolling(false); toast.success("🎨 Your cast art is ready!"); }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artPolling]);

  // ---- Auto-soundtrack (themed AI sound pack: BGM + SFX) ----
  const refreshAudio = async () => {
    try {
      const { data } = await api.get(`/audio/pack-status/${id}`);
      if (data.done && data.audio && Object.keys(data.audio).length) {
        // Pull the freshly-composed audio map into the live config.
        try {
          const { data: g } = await api.get(`/games/${id}`);
          if (g.config) { setConfig(g.config); setGame((p) => ({ ...p, config: g.config })); }
        } catch {}
      }
      return data.done;
    } catch { return true; }
  };
  useEffect(() => {
    if (!game || audioInitRef.current) return;
    audioInitRef.current = true;
    (async () => {
      if (game.audio_generating) {
        const done = await refreshAudio();
        if (!done) setAudioPolling(true);
      } else if (game.config?.audio?.bgm && game.created_at) {
        // Fast path: the pack finished before we entered the studio. Let creators of a freshly
        // made game know sound is here (only for recent creates, so revisits don't nag).
        const ageMin = (Date.now() - new Date(game.created_at).getTime()) / 60000;
        if (ageMin >= 0 && ageMin < 10) toast.success("🎵 Your AI soundtrack is ready — press Play to hear it.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, game]);
  useEffect(() => {
    if (!audioPolling) return;
    const t = setInterval(async () => {
      const done = await refreshAudio();
      if (done) { setAudioPolling(false); toast.success("🎵 Your game's soundtrack is ready!"); }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPolling]);

  // ---- Voice acting (spoken dialogue for VN nodes + NPC lines) ----
  const refreshVoice = async () => {
    try {
      const { data } = await api.get(`/audio/voiceover-status/${id}`);
      if (data.done && data.config) { setConfig(data.config); setGame((p) => ({ ...p, config: data.config })); }
      return data.done;
    } catch { return true; }
  };
  const generateVoiceover = async () => {
    setVoiceBusy(true);
    try {
      const { data } = await api.post("/audio/voiceover", { game_id: id });
      toast.success(`🎙️ Voicing ${data.total} line${data.total === 1 ? "" : "s"} — this runs in the background.`);
      setVoicePolling(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start voice acting.");
    } finally { setVoiceBusy(false); }
  };
  useEffect(() => {
    if (game && game.voice_generating && !voicePolling) setVoicePolling(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);
  useEffect(() => {
    if (!voicePolling) return;
    const t = setInterval(async () => {
      const done = await refreshVoice();
      if (done) { setVoicePolling(false); toast.success("🎙️ Voice acting is ready — your characters now speak!"); }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voicePolling]);

  // Count how many spoken lines exist so we can show/hide the voice-acting panel.
  const voiceLineCount = (() => {
    let n = 0;
    (config?.vn?.nodes || []).forEach((nd) => { if ((nd.text || "").trim()) n += 1; });
    (config?.npcs || []).forEach((npc) => (npc.dialogue || []).slice(0, 4).forEach((d) => { if ((d || "").trim()) n += 1; }));
    return n;
  })();
  const voicedCount = (() => {
    let n = 0;
    (config?.vn?.nodes || []).forEach((nd) => { if (nd.voiceUrl) n += 1; });
    (config?.npcs || []).forEach((npc) => (npc.voiceUrls || []).forEach((u) => { if (u) n += 1; }));
    return n;
  })();


  const revert = async (version) => {
    try {
      const { data } = await api.post(`/games/${id}/revert/${version}`);
      setConfig(data.config);
      setGame((g) => (g ? { ...g, config: data.config } : g));
      setActiveVersion(version);
      toast.success(`Jumped back to version ${version}`);
    } catch { toast.error("Could not revert."); }
  };

  const setField = (path, value) => {
    setConfig((c) => {
      const next = JSON.parse(JSON.stringify(c));
      const parts = path.split(".");
      let o = next;
      for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]];
      o[parts[parts.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const saveConfig = async () => {
    try {
      const { data } = await api.patch(`/games/${id}/config`, { config, summary: "Tuned settings" });
      setConfig(data.config);
      setGame((g) => ({ ...g, config: data.config, versions: [...(g.versions || []), { version: data.version, config: data.config, summary: "Tuned settings", created_at: new Date().toISOString() }] }));
      setDirty(false);
      toast.success("Settings saved!");
    } catch { toast.error("Could not save settings."); }
  };

  const [enhancing, setEnhancing] = useState("");
  const enhance = async (preset) => {
    if (enhancing) return;
    setEnhancing(preset);
    try {
      const { data } = await api.post(`/games/${id}/enhance`, { preset });
      setConfig(data.config);
      setGame((g) => ({ ...g, config: data.config, versions: [...(g.versions || []), { version: data.version, config: data.config, summary: data.summary, created_at: new Date().toISOString() }] }));
      toast.success(data.summary);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEnhancing(""); }
  };

  const [skyboxLoading, setSkyboxLoading] = useState(false);
  const [skyboxPrompt, setSkyboxPrompt] = useState("");
  const genSkybox = async (promptOverride) => {
    if (skyboxLoading) return;
    const prompt = (typeof promptOverride === "string" ? promptOverride : skyboxPrompt) || undefined;
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setSkyboxLoading(true);
    try {
      const { data } = await api.post(`/fal/skybox`, { game_id: id, prompt });
      if (data.config) {
        setConfig(data.config);
        setGame((g) => (g ? { ...g, config: data.config, versions: data.version != null
          ? [...(g.versions || []), { version: data.version, config: data.config, summary: "AI skybox", created_at: new Date().toISOString() }]
          : g.versions } : g));
        undoableConfigChange(prevConfig, prevVersion, data.config, data.version, "AI skybox generated 🌌");
      } else {
        toast.success("AI skybox generated ✨");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Skybox failed"); }
    finally { setSkyboxLoading(false); }
  };
  const applySkyboxPreset = (prompt) => { setSkyboxPrompt(prompt); genSkybox(prompt); };
  const removeSkybox = async () => {
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setSkyboxLoading(true);
    try {
      const { data } = await api.delete(`/fal/skybox/${id}`);
      if (data.config) {
        setConfig(data.config);
        setGame((g) => (g ? { ...g, config: data.config, versions: data.version != null
          ? [...(g.versions || []), { version: data.version, config: data.config, summary: "Removed skybox", created_at: new Date().toISOString() }]
          : g.versions } : g));
        undoableConfigChange(prevConfig, prevVersion, data.config, data.version, "Skybox removed");
      } else {
        toast.success("Skybox removed");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSkyboxLoading(false); }
  };

  // AI PBR surface textures (ground / structures / props)
  const [texBusy, setTexBusy] = useState("");
  const [texPrompts, setTexPrompts] = useState({ ground: "", structures: "", props: "" });
  const genTexture = async (surface, promptOverride) => {
    if (texBusy) return;
    const prompt = (typeof promptOverride === "string" ? promptOverride : texPrompts[surface]) || undefined;
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setTexBusy(surface);
    try {
      const { data } = await api.post(`/fal/texture`, { game_id: id, surface, prompt });
      if (data.config) {
        setConfig(data.config);
        setGame((g) => (g ? { ...g, config: data.config, versions: data.version != null
          ? [...(g.versions || []), { version: data.version, config: data.config, summary: `AI ${surface} texture`, created_at: new Date().toISOString() }]
          : g.versions } : g));
        undoableConfigChange(prevConfig, prevVersion, data.config, data.version, `AI ${surface} texture generated 🎨`);
      } else { toast.success("AI texture generated ✨"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Texture failed"); }
    finally { setTexBusy(""); }
  };
  const removeTexture = async (surface) => {
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setTexBusy(surface);
    try {
      const { data } = await api.delete(`/fal/texture/${id}/${surface}`);
      if (data.config) {
        setConfig(data.config);
        setGame((g) => (g ? { ...g, config: data.config, versions: data.version != null
          ? [...(g.versions || []), { version: data.version, config: data.config, summary: `Removed ${surface} texture`, created_at: new Date().toISOString() }]
          : g.versions } : g));
        undoableConfigChange(prevConfig, prevVersion, data.config, data.version, "Texture removed");
      } else { toast.success("Texture removed"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTexBusy(""); }
  };
  // Texture Variations — generate 3 options, then pick one to apply
  const [texVariants, setTexVariants] = useState({});
  const genTextureVariants = async (surface) => {
    if (texBusy) return;
    const prompt = texPrompts[surface] || undefined;
    setTexBusy(surface);
    try {
      const { data } = await api.post(`/fal/texture/variants`, { game_id: id, surface, prompt });
      setTexVariants((v) => ({ ...v, [surface]: data.variants || [] }));
      toast.success("3 options ready — pick your favourite ✨");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't make options"); }
    finally { setTexBusy(""); }
  };
  const selectTextureVariant = async (surface, index) => {
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setTexBusy(surface);
    try {
      const { data } = await api.post(`/fal/texture/select`, { game_id: id, surface, index });
      if (data.config) {
        setConfig(data.config);
        setGame((g) => (g ? { ...g, config: data.config, versions: data.version != null
          ? [...(g.versions || []), { version: data.version, config: data.config, summary: `AI ${surface} texture`, created_at: new Date().toISOString() }]
          : g.versions } : g));
        undoableConfigChange(prevConfig, prevVersion, data.config, data.version, `AI ${surface} texture applied 🎨`);
      }
      setTexVariants((v) => ({ ...v, [surface]: null }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTexBusy(""); }
  };

  // Produce Everything — one-tap full AI world (skybox + textures + weather + props)
  const [producing, setProducing] = useState(false);
  const [produceStage, setProduceStage] = useState("");
  const [produceProgress, setProduceProgress] = useState(0);
  const [producePrompt, setProducePrompt] = useState("");
  const [planPreview, setPlanPreview] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const previewPlan = async () => {
    if (planBusy) return;
    setPlanBusy(true);
    try {
      const { data } = await api.post(`/fal/plan-world`, { game_id: id, prompt: producePrompt || undefined });
      setPlanPreview(data.plan || null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't design a plan"); }
    finally { setPlanBusy(false); }
  };
  const updatePlanField = (k, v) => setPlanPreview((p) => ({ ...(p || {}), [k]: v }));
  const produceWorld = async () => {
    if (producing) return;
    const approvedPlan = planPreview;
    setProducing(true); setProduceStage("starting"); setProduceProgress(2); setPlanPreview(null);
    const poll = async () => {
      try {
        const { data } = await api.get(`/fal/produce-world/${id}`);
        setProduceStage(data.stage || ""); setProduceProgress(data.progress || 0);
        if (data.status === "done") {
          const res = await api.get(`/fal/produce-world/${id}/result`);
          if (res.data?.config) { setConfig(res.data.config); setGame((g) => (g ? { ...g, config: res.data.config } : g)); }
          toast.success("Your full AI world is ready 🌍✨");
          setProducing(false); return;
        }
        if (data.status === "failed") { toast.error(data.error || "World production failed"); setProducing(false); return; }
        setTimeout(poll, 2500);
      } catch (e) { setTimeout(poll, 3000); }
    };
    try {
      await api.post(`/fal/produce-world`, { game_id: id, prompt: producePrompt || undefined, plan: approvedPlan || undefined });
      setTimeout(poll, 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start production");
      setProducing(false);
    }
  };
  // Texture Library — save a surface set once, reuse across games
  const [texSets, setTexSets] = useState([]);
  const [libBusy, setLibBusy] = useState(false);
  const loadTexSets = async () => { try { const { data } = await api.get(`/fal/texture-library`); setTexSets(data.sets || []); } catch { /* noop */ } };
  useEffect(() => { loadTexSets(); }, [id]);
  const saveTexSet = async () => {
    setLibBusy(true);
    try { const { data } = await api.post(`/fal/texture-library`, { game_id: id }); toast.success(`Saved "${data.name}" to your library`); loadTexSets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save set"); }
    finally { setLibBusy(false); }
  };
  const applyTexSet = async (setId) => {
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setLibBusy(true);
    try {
      const { data } = await api.post(`/fal/texture-library/${setId}/apply`, { game_id: id });
      if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); undoableConfigChange(prevConfig, prevVersion, data.config, data.version, "Applied texture set 🎨"); }
      toast.success("Texture set applied");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLibBusy(false); }
  };
  const deleteTexSet = async (setId) => { try { await api.delete(`/fal/texture-library/${setId}`); loadTexSets(); toast.success("Set removed"); } catch { toast.error("Couldn't remove set"); } };
  // Library sharing — public community gallery
  const [publicSets, setPublicSets] = useState([]);
  const [showCommunity, setShowCommunity] = useState(false);
  const loadPublicSets = async () => { try { const { data } = await api.get(`/fal/texture-library/public`); setPublicSets(data.sets || []); } catch { /* noop */ } };
  const toggleCommunity = () => { const n = !showCommunity; setShowCommunity(n); if (n) loadPublicSets(); };
  const publishTexSet = async (setId, makePublic) => {
    setLibBusy(true);
    try {
      await api.post(`/fal/texture-library/${setId}/${makePublic ? "publish" : "unpublish"}`);
      toast.success(makePublic ? "Shared to the community gallery 🌍" : "Removed from the gallery");
      loadTexSets(); if (showCommunity) loadPublicSets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLibBusy(false); }
  };
  const likeTexSet = async (setId) => {
    try { const { data } = await api.post(`/fal/texture-library/${setId}/like`); setPublicSets((ps) => ps.map((s) => s.id === setId ? { ...s, liked: data.liked, likes: data.likes } : s)); } catch { /* noop */ }
  };
  // Shareable World Presets — the whole look (skybox + surfaces + atmosphere + emitters)
  const [presets, setPresets] = useState([]);
  const [publicPresets, setPublicPresets] = useState([]);
  const [featuredPreset, setFeaturedPreset] = useState(null);
  const loadPresets = async () => { try { const { data } = await api.get(`/fal/world-preset`); setPresets(data.presets || []); } catch { /* noop */ } };
  const loadPublicPresets = async () => { try { const { data } = await api.get(`/fal/world-preset/public`); setPublicPresets(data.presets || []); setFeaturedPreset(data.featured || null); } catch { /* noop */ } };
  useEffect(() => { loadPresets(); }, [id]);
  const savePreset = async (name) => {
    setLibBusy(true);
    try { const { data } = await api.post(`/fal/world-preset`, { game_id: id, name: name || undefined }); toast.success(`Saved world preset "${data.name}" 🌍`); loadPresets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save preset"); }
    finally { setLibBusy(false); }
  };
  const applyPreset = async (pid) => {
    const prevConfig = config; const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setLibBusy(true);
    try {
      const { data } = await api.post(`/fal/world-preset/${pid}/apply`, { game_id: id });
      if (data.config) { setConfig(data.config); setGame((g) => (g ? { ...g, config: data.config } : g)); undoableConfigChange(prevConfig, prevVersion, data.config, data.version, "Applied world preset 🌍"); }
      toast.success("World preset applied 🌍✨");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLibBusy(false); }
  };
  const publishPreset = async (pid, pub) => {
    try { await api.post(`/fal/world-preset/${pid}/${pub ? "publish" : "unpublish"}`); toast.success(pub ? "Shared world to the community 🌍" : "Removed from the gallery"); loadPresets(); loadPublicPresets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const likePreset = async (pid) => { try { const { data } = await api.post(`/fal/world-preset/${pid}/like`); setPublicPresets((ps) => ps.map((p) => p.id === pid ? { ...p, liked: data.liked, likes: data.likes } : p)); setFeaturedPreset((f) => (f && f.id === pid ? { ...f, liked: data.liked, likes: data.likes } : f)); } catch { /* noop */ } };
  const deletePreset = async (pid) => { try { await api.delete(`/fal/world-preset/${pid}`); loadPresets(); toast.success("Preset removed"); } catch { toast.error("Couldn't remove preset"); } };
  const featurePreset = async (pid) => {
    try { const { data } = await api.post(`/fal/world-preset/${pid}/feature`); toast.success(data.featured ? "Pinned as ⭐ Preset of the Week" : "Unpinned Preset of the Week"); loadPublicPresets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update feature"); }
  };
  // Pre-fill texture prompts from the game's remembered per-surface prompts (so a single surface can be re-rolled in the same style)
  useEffect(() => {
    const mp = config?.world?.materialPrompts;
    if (mp) setTexPrompts((t) => ({ ground: t.ground || mp.ground || "", structures: t.structures || mp.structures || "", props: t.props || mp.props || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.world?.materialPrompts]);
  const runPlaytest = async () => {
    setPlaytest("loading");
    try {
      const { data } = await api.post(`/games/${id}/playtest`);
      setPlaytest(data);
    } catch { setPlaytest(null); toast.error("Playtest failed"); }
  };

  const genArt = async (target) => {
    if (artLoading) return;
    const prevConfig = config;
    const prevVersion = (game?.versions || []).slice(-1)[0]?.version ?? null;
    setArtLoading(target);
    try {
      const { data } = await api.post(`/games/${id}/generate-art`, { target, prompt: artPrompt || undefined });
      setConfig(data.config);
      setGame((g) => ({ ...g, config: data.config, versions: [...(g.versions || []), { version: data.version, config: data.config, summary: `AI art for ${target}`, created_at: new Date().toISOString() }] }));
      undoableConfigChange(prevConfig, prevVersion, data.config, data.version, `Studio drew your ${target} 🎨`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setArtLoading(""); }
  };

  const openLibrary = async () => {
    setLibOpen(true);
    try { const { data } = await api.get("/characters"); setLibChars(data); } catch { /* ignore */ }
  };
  const applyLibChar = async (charId) => {
    try {
      const { data } = await api.post(`/characters/${charId}/apply`, { game_id: id, target: libTarget });
      setConfig(data.config);
      setGame((g) => ({ ...g, config: data.config, versions: [...(g.versions || []), { version: data.version, config: data.config, summary: `Added character as ${libTarget}`, created_at: new Date().toISOString() }] }));
      toast.success(`Character added as ${libTarget}! 🎮`);
      setLibOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyConfigUpdate = (data, summary) => {
    setConfig(data.config);
    setGame((g) => ({ ...g, config: data.config, story: data.story || g.story, versions: [...(g.versions || []), { version: data.version, config: data.config, summary, created_at: new Date().toISOString() }] }));
  };

  const buildWorld = async () => {
    if (worldLoading || worldPrompt.trim().length < 2) return;
    setWorldLoading(true);
    try {
      await autoCheckpoint(`Before world: ${worldPrompt.slice(0, 36)}`);
      const { data } = await api.post(`/games/${id}/world`, { prompt: worldPrompt });
      applyConfigUpdate(data, data.summary);
      setMessages((m) => [...m, { role: "studio", text: data.summary }]);
      toast.success("New world built! 🌍");
      setWorldPrompt("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWorldLoading(false); }
  };

  const buildStory = async (prompt) => {
    if (storyLoading) return;
    setStoryLoading(true);
    try {
      const { data } = await api.post(`/games/${id}/story`, prompt ? { prompt } : {});
      applyConfigUpdate(data, data.summary);
      toast.success("Story & missions written! 📖");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStoryLoading(false); }
  };

  const buildEncounters = async () => {
    if (encLoading) return;
    setEncLoading(true);
    try {
      const { data } = await api.post(`/games/${id}/encounters`, { waves: encWaves });
      applyConfigUpdate(data, data.summary);
      toast.success(`Survival mode ready — ${data.encounters?.waves?.length || encWaves} waves! 🌊`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEncLoading(false); }
  };

  const disableEncounters = async () => {
    if (encLoading) return;
    setEncLoading(true);
    try {
      const { data } = await api.post(`/games/${id}/encounters`, { disable: true });
      applyConfigUpdate(data, data.summary);
      toast.success("Survival mode turned off — back to endless arena.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEncLoading(false); }
  };

  const buildQuests = async () => {
    if (questLoading) return;
    setQuestLoading(true);
    try {
      const { data } = await api.post(`/games/${id}/quests`, { count: questCount });
      applyConfigUpdate(data, data.summary);
      toast.success(`Quest chain ready — ${data.quests?.nodes?.length || questCount} quests! 🗺️`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setQuestLoading(false); }
  };

  const disableQuests = async () => {
    if (questLoading) return;
    setQuestLoading(true);
    try {
      const { data } = await api.post(`/games/${id}/quests`, { disable: true });
      applyConfigUpdate(data, data.summary);
      toast.success("Quest chain removed.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setQuestLoading(false); }
  };

  const openNpcChat = (idx) => {
    const npc = (config.npcs || [])[idx];
    setNpcIdx(idx);
    setNpcSession(null);
    setNpcMsgs(npc ? [{ role: "npc", text: npc.dialogue?.[0] || `Hi! I'm ${npc.name || "your friend"}.` }] : []);
    setNpcOpen(true);
  };
  const sendNpc = async () => {
    const message = npcInput.trim();
    if (!message || npcSending) return;
    setNpcInput("");
    setNpcMsgs((m) => [...m, { role: "player", text: message }]);
    setNpcSending(true);
    try {
      const { data } = await api.post(`/games/${id}/npc-chat`, { npc_index: npcIdx, message, session: npcSession });
      setNpcSession(data.session);
      setNpcMsgs((m) => [...m, { role: "npc", text: data.reply }]);
    } catch (e) {
      setNpcMsgs((m) => [...m, { role: "npc", text: formatApiError(e.response?.data?.detail) }]);
    } finally { setNpcSending(false); }
  };

  const speak = async (text, key, voice = "nova") => {
    if (!text || speaking) return;
    setSpeaking(key);
    try {
      const { data } = await api.post("/tts", { text: String(text).slice(0, 600), voice });
      new Audio(data.audio).play().catch(() => {});
    } catch (e) { toast.error("Couldn't play the voice."); }
    finally { setTimeout(() => setSpeaking(""), 400); }
  };

  const publish = async () => {
    try {
      let cover = null;
      try { cover = await canvasRef.current?.capture(); } catch {}
      const { data } = await api.post(`/games/${id}/publish`, { ...pubForm, cover_image: cover });
      setPublishOpen(false);
      if (data.status === "published") {
        confetti({ particleCount: 160, spread: 80, origin: { y: 0.6 } });
      }
      toast.success(data.message);
      setGame((g) => ({ ...g, status: data.status }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!game || !config) {
    return <div className="min-h-[80vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  const statusBadge = {
    draft: { label: "Draft", cls: "bg-[hsl(var(--secondary))]" },
    published: { label: "Published", cls: "bg-emerald-500 text-white" },
    pending_approval: { label: "Waiting for parent", cls: "bg-amber-500 text-white" },
    pending_moderation: { label: "In review", cls: "bg-amber-500 text-white" },
    rejected: { label: "Needs changes", cls: "bg-red-500 text-white" },
  }[game.status] || { label: game.status, cls: "bg-[hsl(var(--secondary))]" };

  const hasMeshyModels = !!(
    config.player?.model_url || config.enemy?.model_url || config.boss?.model_url ||
    (config.npcs || []).some((n) => n.model_url)
  ) || (models || []).some((m) => m.status === "succeeded");

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/50">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/games")} data-testid="builder-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-lg font-bold truncate flex items-center gap-2">{config.player?.emoji} {config.title}</h1>
            <div className="flex items-center gap-1.5">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${statusBadge.cls}`} data-testid="game-status">{statusBadge.label}</span>
              {hasMeshyModels && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" data-testid="meshy-built-badge" title="Characters generated as real 3D models with Meshy AI">
                  <Boxes className="w-3 h-3" /> AI-built · Meshy 3D
                </span>
              )}
              {artPolling && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-amber-500 to-pink-500 animate-pulse" data-testid="art-generating-badge" title="Drawing real AI sprite art for your cast">
                  <Loader2 className="w-3 h-3 animate-spin" /> Drawing cast art…
                </span>
              )}
              {audioPolling ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-fuchsia-500 to-pink-500 animate-pulse" data-testid="audio-generating-badge" title="Composing your game's AI soundtrack (music + SFX)">
                  <Loader2 className="w-3 h-3 animate-spin" /> Composing soundtrack…
                </span>
              ) : config.audio?.bgm ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-fuchsia-700 dark:text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-500/40" data-testid="soundtrack-ready-badge" title="This game ships with an AI-composed soundtrack — press Play to hear it">
                  <Volume2 className="w-3 h-3" /> Soundtrack ready
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <WorkforcePulse gameId={id} />
          <div className="flex items-center rounded-full border border-[hsl(var(--border))] overflow-hidden" data-testid="undo-redo-group">
            <button onClick={undo} disabled={histState.idx <= 0} title="Undo" data-testid="undo-btn"
              className="px-2.5 h-8 flex items-center hover:bg-[hsl(var(--secondary))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Undo2 className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-[hsl(var(--border))]" />
            <button onClick={redo} disabled={histState.idx >= histState.len - 1} title="Redo" data-testid="redo-btn"
              className="px-2.5 h-8 flex items-center hover:bg-[hsl(var(--secondary))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Redo2 className="w-4 h-4" />
            </button>
          </div>
          <Button size="sm" onClick={() => { setAmazingTarget("game"); setAmazingOpen(true); }} data-testid="make-amazing-btn"
            className="rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 hover:opacity-90 text-black font-bold shadow-[0_4px_20px_-4px_rgba(217,70,239,.6)]">
            <Sparkles className="w-4 h-4" /> Make It Amazing
          </Button>
          <Button size="sm" onClick={() => setTestPlayOpen(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="test-play-btn">
            <FlaskConical className="w-4 h-4" /> Test Play
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/app/levels/${id}`)} className="rounded-full hidden md:flex" data-testid="levels-btn">
            <Layers className="w-4 h-4" /> Levels{game?.levels_meta?.count ? ` (${game.levels_meta.count})` : ""}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full" data-testid="builder-more-btn">
                <MoreHorizontal className="w-4 h-4" /> More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => runPlaytest()} data-testid="playtest-btn">
                <FlaskConical className="w-4 h-4 mr-2" /> AI Playtest
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/project/${id}`)} data-testid="project-hub-btn">
                <LayoutDashboard className="w-4 h-4 mr-2" /> Project Hub
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/levels/${id}`)} className="md:hidden" data-testid="levels-menu-item">
                <Layers className="w-4 h-4 mr-2" /> Levels{game?.levels_meta?.count ? ` (${game.levels_meta.count})` : ""}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/compiler/${id}`)} data-testid="compiler-menu-item">
                <Boxes className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Game Compiler 🧭
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/autopilot/${id}`)} data-testid="autopilot-menu-item">
                <Wand2 className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> AI Game Director
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/map/${id}`)} data-testid="map-studio-menu-item">
                <MapIcon className="w-4 h-4 mr-2" /> Map Studio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/studio/${id}`)} data-testid="ai-studio-menu-item">
                <Users className="w-4 h-4 mr-2" /> AI Studio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/living-world/${id}`)} data-testid="living-world-menu-item">
                <Globe2 className="w-4 h-4 mr-2" /> Living World
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/cinematic/${id}`)} data-testid="cinematic-menu-item">
                <Clapperboard className="w-4 h-4 mr-2" /> Cinematic Studio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/weather/${id}`)} data-testid="weather-menu-item">
                <CloudSun className="w-4 h-4 mr-2" /> Weather & Sky
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/materials/${id}`)} data-testid="materials-menu-item">
                <Layers className="w-4 h-4 mr-2" /> Material Studio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/gtm/${id}`)} data-testid="gtm-menu-item">
                <Rocket className="w-4 h-4 mr-2" /> Go-to-Market Kit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/qa/${id}`)} data-testid="qa-menu-item">
                <Bug className="w-4 h-4 mr-2" /> AI Playtester
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/ir/${id}`)} data-testid="game-ir-menu-item">
                <FileCode2 className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Game IR
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/brain/${id}`)} data-testid="code-brain-menu-item">
                <BrainCircuit className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Code Brain
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/director/${id}`)} data-testid="game-director-menu-item">
                <Clapperboard className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Game Director (AI team)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/visual/${id}`)} data-testid="visual-director-menu-item">
                <Palette className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Visual Director ✨
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/feel/${id}`)} data-testid="game-feel-menu-item">
                <Zap className="w-4 h-4 mr-2 text-[hsl(var(--primary))]" /> Game Feel &amp; Gold Gate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/app/bridge/${id}`)} data-testid="export-unity-btn">
                <Boxes className="w-4 h-4 mr-2" /> Export to Unity
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShareOpen(true)} data-testid="share-export-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share & Export
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCollabOpen(true)} data-testid="collaborators-btn">
                <Users className="w-4 h-4 mr-2" /> Collaborators
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={() => navigate(`/app/publish/${id}`)} data-testid="publish-open-btn"
            className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90">
            <Rocket className="w-4 h-4" /> Publish
          </Button>
        </div>
      </div>

      <MakeItAmazingDialog gameId={id} open={amazingOpen} onOpenChange={setAmazingOpen} defaultTarget={amazingTarget}
        onApplied={(cfg) => { setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g)); }} />

      <BuilderWelcome title={config.title}
        onAmazing={() => { setAmazingTarget("game"); setAmazingOpen(true); }}
        onTestPlay={() => setTestPlayOpen(true)}
        onDirector={() => navigate(`/app/autopilot/${id}`)} />

      <div className="grid lg:grid-cols-12 gap-4 p-4 lg:p-6 flex-1">
        {/* Left: settings/plan */}
        <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
          <BuilderPanels
            id={id} game={game} config={config} setConfig={setConfig} setField={setField} is3D={is3D} isBase={isBase} dirty={dirty} saveConfig={saveConfig}
            artPrompt={artPrompt} setArtPrompt={setArtPrompt} artLoading={artLoading} genArt={genArt}
            openLibrary={openLibrary} libOpen={libOpen} setLibOpen={setLibOpen} libChars={libChars} libTarget={libTarget} setLibTarget={setLibTarget} applyLibChar={applyLibChar}
            models={models} genCastBusy={genCastBusy} modelPolling={modelPolling} castStyle={castStyle} setCastStyle={setCastStyle} generate3DCast={generate3DCast} rigCast={rigCast} rigBusy={rigBusy}
            enhance={enhance} enhancing={enhancing}
            skyboxPrompt={skyboxPrompt} setSkyboxPrompt={setSkyboxPrompt} skyboxLoading={skyboxLoading} genSkybox={genSkybox} applySkyboxPreset={applySkyboxPreset} removeSkybox={removeSkybox}
            texBusy={texBusy} texPrompts={texPrompts} setTexPrompts={setTexPrompts} genTexture={genTexture} removeTexture={removeTexture}
            texVariants={texVariants} genTextureVariants={genTextureVariants} selectTextureVariant={selectTextureVariant}
            producing={producing} produceStage={produceStage} produceProgress={produceProgress} producePrompt={producePrompt} setProducePrompt={setProducePrompt} produceWorld={produceWorld}
            planPreview={planPreview} planBusy={planBusy} previewPlan={previewPlan} updatePlanField={updatePlanField} clearPlan={() => setPlanPreview(null)}
            texSets={texSets} libBusy={libBusy} saveTexSet={saveTexSet} applyTexSet={applyTexSet} deleteTexSet={deleteTexSet}
            publicSets={publicSets} showCommunity={showCommunity} toggleCommunity={toggleCommunity} publishTexSet={publishTexSet} likeTexSet={likeTexSet}
            presets={presets} publicPresets={publicPresets} featuredPreset={featuredPreset} isStaff={isStaff} loadPublicPresets={loadPublicPresets} savePreset={savePreset} applyPreset={applyPreset} publishPreset={publishPreset} likePreset={likePreset} deletePreset={deletePreset} featurePreset={featurePreset}
            worldPrompt={worldPrompt} setWorldPrompt={setWorldPrompt} worldLoading={worldLoading} buildWorld={buildWorld}
            openNpcChat={openNpcChat}
            voiceLineCount={voiceLineCount} voicedCount={voicedCount} voicePolling={voicePolling} voiceBusy={voiceBusy} generateVoiceover={generateVoiceover}
            buildStory={buildStory} storyLoading={storyLoading} speak={speak} speaking={speaking}
            questCount={questCount} setQuestCount={setQuestCount} buildQuests={buildQuests} questLoading={questLoading} disableQuests={disableQuests}
            encWaves={encWaves} setEncWaves={setEncWaves} buildEncounters={buildEncounters} encLoading={encLoading} disableEncounters={disableEncounters}
            onImprove={(t) => { setAmazingTarget(t || "game"); setAmazingOpen(true); }}
          />

          {playtest && (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="playtest-report">
              <div className="font-bold text-sm mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4" /> AI Playtest report</div>
              {playtest === "loading" ? (
                <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Studio is playing your game…</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-bold" data-testid="playtest-fun">
                      <Star className="w-3.5 h-3.5 fill-current" /> {playtest.fun_score}/10 fun
                    </div>
                    <Badge variant="secondary">{playtest.difficulty}</Badge>
                    {playtest.est_win_rate && <span className="text-xs text-[hsl(var(--muted-foreground))]">~{playtest.est_win_rate} win</span>}
                  </div>
                  {playtest.verdict && <p className="text-[hsl(var(--muted-foreground))]">{playtest.verdict}</p>}
                  {playtest.checks?.length > 0 && (
                    <ul className="space-y-1.5">
                      {playtest.checks.map((c, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {c.status === "pass" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                          {c.name}
                        </li>
                      ))}
                    </ul>
                  )}
                  {playtest.issues?.length > 0 && (
                    <div><div className="text-xs font-bold text-amber-500 mb-1">Watch outs</div>
                      <ul className="list-disc pl-4 text-[hsl(var(--muted-foreground))] space-y-0.5">{playtest.issues.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                  )}
                  {playtest.suggestions?.length > 0 && (
                    <div><div className="text-xs font-bold mb-1.5">One-tap improvements</div>
                      <div className="flex flex-wrap gap-1.5">
                        {playtest.suggestions.map((s, i) => {
                          const instr = typeof s === "string" ? s : s.instruction;
                          const label = typeof s === "string" ? s : (s.title || s.instruction);
                          return (
                            <button key={i} onClick={() => sendEdit(instr)} disabled={editing} data-testid={`playtest-suggestion-${i}`}
                              className="text-[11px] px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 font-medium">
                              ✨ {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center: preview */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-bold"><Gamepad2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Live Preview
              {config?.dimension === "3d" && (
                <button
                  data-testid="preview-intro-btn"
                  onClick={() => { try { canvasRef.current?.playCutscene("intro"); } catch {} toast.success("🎬 Rolling the intro cutscene…"); }}
                  className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25 transition-colors flex items-center gap-1"
                  title="Watch this game's opening cutscene without leaving the editor"
                >
                  <Film className="w-3 h-3" /> Preview intro
                </button>
              )}
              {comparePreview && <span className={`${config?.dimension === "3d" ? "" : "ml-auto"} text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1`} data-testid="compare-banner"><Eye className="w-3 h-3" /> Showing BEFORE the last change</span>}
            </div>
            <div className="relative">
              <GameCanvas ref={canvasRef} config={config} previewConfig={livePreview || comparePreview} previewMode onPick={() => {}} />
              {!comparePreview && !["quiz", "memory", "visualnovel"].includes(config.template) && (
                <LiveCreatorBar
                  gameId={id}
                  config={livePreview || config}
                  canvasRef={canvasRef}
                  is3d={config.dimension === "3d"}
                  onPreview={setLivePreview}
                  onClear={() => setLivePreview(null)}
                  onAccept={(cfg) => { setLivePreview(null); setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g)); }}
                  initialInstruction={pendingEdit}
                />
              )}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] text-center mt-2">{config.instructions}</p>
            {botSupported && !comparePreview && (
              <div className="flex items-center justify-center gap-2 mt-3" data-testid="builder-bot-bar">
                {game?.robot_best > 0 && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${game.robot_best >= 80 ? "bg-violet-500/15 text-violet-500 dark:text-violet-300 border border-violet-500/40" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`} data-testid="builder-robot-score" title="Latest AI robot playtest score">
                    🤖 {game.robot_best}/100{game.robot_best >= 80 ? " · Certified" : ""}
                  </span>
                )}
                <AutoPlaytestButton canvasRef={canvasRef} gameId={id} canFix autoRun={!!game?.auto_robot_pending} onFixed={(cfg) => { setConfig(cfg); setGame((g) => (g ? { ...g, config: cfg } : g)); }} onReport={(r) => setGame((g) => (g && r ? { ...g, robot_best: Math.max(g.robot_best || 0, r.score || 0) } : g))} />
                {visualQa && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${visualQa.demoLook ? "bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/40" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/40"}`} data-testid="builder-visualqa" title={(visualQa.issues || []).map((i) => `• ${i.msg}`).join("\n") || "No visual issues detected"}>
                    {visualQa.demoLook ? `⚠ Demo-look · ${(visualQa.issues || []).length}` : "✓ Visual QA"}
                  </span>
                )}
                {visualQa && (
                  <button onClick={() => runVisionQa()} disabled={visionBusy || !visualQa.thumb} data-testid="run-vision-qa-btn"
                    title="GPT-5 vision inspects a live screenshot for subtle art defects a geometry scan can't catch"
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-300 hover:bg-violet-500/20 disabled:opacity-50 transition-colors">
                    {visionBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} AI Vision QA
                  </button>
                )}
                {visualQa?.ai && (
                  <span data-testid="builder-visionqa"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${(visualQa.ai.defects || []).some((d) => d.severity === "high") ? "bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/40" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/40"}`}
                    title={(visualQa.ai.defects || []).map((d) => `• [${d.severity}] ${d.finding}`).join("\n") || visualQa.ai.verdict || "No AI-detected art defects"}>
                    <Eye className="w-3 h-3" /> {(visualQa.ai.defects || []).length ? `${(visualQa.ai.defects || []).length} art issue(s)` : "Vision clean"}
                  </span>
                )}
                {(visualQa?.ai?.defects || []).some((d) => d.severity === "high" || d.severity === "medium") && (
                  <button onClick={runVisualAutofix} disabled={autofixBusy} data-testid="visual-autofix-btn"
                    title="Auto-fix the lighting/texture issues the AI vision pass found (adjusts exposure, grade, shadows, detail)"
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300 hover:bg-sky-500/20 disabled:opacity-50 transition-colors">
                    {autofixBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Auto-fix visuals
                  </button>
                )}
                {uniqueness && typeof uniqueness.score === "number" && (
                  <span data-testid="builder-uniqueness"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${uniqueness.score >= 60 ? "bg-violet-500/15 text-violet-600 dark:text-violet-300 border border-violet-500/40" : "bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/40"}`}
                    title={uniqueness.note || ""}>
                    <Zap className="w-3 h-3" /> Unique {uniqueness.score}/100
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: chat-to-edit + versions */}
        <div className="lg:col-span-3 order-3 flex flex-col">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] flex flex-col h-[560px]">
            <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center gap-2 font-bold text-sm">
              <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Chat with Studio
              <button onClick={() => setFxOpen(true)} data-testid="quickfx-open-btn"
                className="ml-auto inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] text-white text-[11px] font-semibold px-2.5 py-1 hover:opacity-90 transition-opacity">
                <Boxes className="w-3.5 h-3.5" /> Quick FX
              </button>
            </div>
            <Sheet open={fxOpen} onOpenChange={setFxOpen}>
              <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="quickfx-sheet">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2"><Boxes className="w-5 h-5 text-[hsl(var(--primary))]" /> Quick FX</SheetTitle>
                  <SheetDescription>Instant one-tap upgrades — no typing needed. Each tap saves a new undoable version.</SheetDescription>
                </SheetHeader>
                <div className="mt-5">
                  <QuickFXTray gameId={id} onApplied={applyQuickFx} />
                </div>
              </SheetContent>
            </Sheet>
            <div ref={chatLogRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "space-y-1"}`}>
                    <div className={`px-3.5 py-2.5 rounded-2xl text-sm ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white rounded-br-sm" : "bg-[hsl(var(--secondary))] rounded-bl-sm"}`}>
                      {m.text}
                    </div>
                    {(m.revertTo || m.before) && (
                      <div className="flex items-center gap-3">
                        {m.revertTo && (
                          <button onClick={() => revertToCheckpoint(m.revertTo)} data-testid={`revert-change-${i}`}
                            className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] font-medium transition-colors">
                            <Undo2 className="w-3 h-3" /> Undo this change
                          </button>
                        )}
                        {m.before && (
                          <button onClick={() => toggleCompare(i, m.before)} data-testid={`compare-change-${i}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium transition-colors ${compareIdx === i ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"}`}>
                            <Eye className="w-3 h-3" /> {compareIdx === i ? "Back to current" : "Compare before"}
                          </button>
                        )}
                      </div>
                    )}
                    {m.suggestions?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`chat-suggestions-${i}`}>
                        <span className="text-[10px] text-[hsl(var(--muted-foreground))] w-full">✨ Try next:</span>
                        {m.suggestions.map((sug, si) => (
                          <button key={si} onClick={() => sendEdit(sug)} disabled={editing} data-testid={`chat-suggestion-${i}-${si}`}
                            className="inline-flex items-center rounded-full border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 px-2.5 py-1 text-[11px] font-medium hover:bg-[hsl(var(--primary))]/10 transition-colors disabled:opacity-50">
                            {sug}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {editing && (
                <div className="flex justify-start"><div className="px-3.5 py-2.5 rounded-2xl bg-[hsl(var(--secondary))] text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Studio is editing…</div></div>
              )}
              <div />
            </div>
            <div className="p-3 border-t border-[hsl(var(--border))]">
              {autopilotHint && (
                <div data-testid="autopilot-chat-suggestion" className="mb-2 rounded-xl border border-[hsl(var(--primary))]/40 bg-gradient-to-br from-[#7c3aed]/10 to-[#3b9eff]/10 p-2.5">
                  <div className="flex items-start gap-2">
                    <Wand2 className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold leading-snug">This looks like a big build. Want the AI Director to plan &amp; build it step-by-step?</p>
                      {outlineBusy && (
                        <div className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Sketching a quick 3-step outline…</div>
                      )}
                      {outlineSteps && outlineSteps.length > 0 && (
                        <ol className="mt-2 space-y-1" data-testid="autopilot-chat-outline">
                          {outlineSteps.map((t, i) => (
                            <li key={i} className="text-[11px] flex items-center gap-1.5" data-testid={`autopilot-chat-outline-${i}`}>
                              <span className="grid place-items-center w-4 h-4 rounded bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] font-bold text-[9px] shrink-0">{i + 1}</span>
                              <span className="truncate">{t}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                      <div className="flex items-center gap-2.5 mt-2 flex-wrap">
                        <Button size="sm" data-testid="autopilot-chat-handoff-btn"
                          onClick={() => navigate(`/app/autopilot/${id}?goal=${encodeURIComponent(autopilotHint)}`)}
                          className="h-7 rounded-full text-xs bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] hover:opacity-90 text-white">
                          <Wand2 className="w-3.5 h-3.5" /> Use AI Director
                        </Button>
                        {!outlineSteps && (
                          <button onClick={outlinePreview} disabled={outlineBusy} data-testid="autopilot-chat-preview-btn"
                            className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--primary))] hover:underline font-medium disabled:opacity-50">
                            <Eye className="w-3 h-3" /> Preview steps
                          </button>
                        )}
                        <button onClick={() => { setAutopilotHint(null); setOutlineSteps(null); }} data-testid="autopilot-chat-dismiss-btn"
                          className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] font-medium">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mb-2">
                <button onClick={() => navigate(`/app/compiler/${id}?compile=1`)} disabled={editing} data-testid="chat-compile-idea-btn"
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold text-white bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed] hover:opacity-90 disabled:opacity-50">
                  <Boxes className="w-3 h-3" /> Compile my idea
                </button>
                {QUICK.map((q) => (
                  <button key={q} onClick={() => sendEdit(q)} disabled={editing} data-testid={`quick-${q.slice(0, 8)}`}
                    className="text-[11px] px-2 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 font-medium">{q}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={listening ? stop : start} className="shrink-0" data-testid="chat-mic-btn">
                  {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendEdit()}
                  placeholder="Tell Studio what to change…" data-testid="chat-input" className="font-mono text-sm" />
                <Button onClick={() => sendEdit()} disabled={editing} size="icon" className="shrink-0" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>

          {/* Undo History — stacked timeline: jump back to any step in one tap */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] mt-4 p-4">
            <div className="flex items-center gap-2 font-bold text-sm mb-1"><History className="w-4 h-4" /> Undo History</div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3">Every step is saved — jump back to any point in one tap.</p>
            <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="undo-history">
              {(() => {
                const vers = [...(game.versions || [])].reverse();
                const curV = activeVersion ?? vers[0]?.version;
                const curIdx = Math.max(0, vers.findIndex((v) => v.version === curV));
                return vers.map((v, idx) => {
                  const isCurrent = v.version === curV;
                  const delta = idx - curIdx;
                  const label = delta === 1 ? "Undo" : delta === -1 ? "Redo" : delta > 0 ? `Back ${delta}` : `Fwd ${-delta}`;
                  return (
                    <div key={v.version} data-testid={`history-v${v.version}`}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${isCurrent ? "bg-[hsl(var(--primary))]/10" : "hover:bg-[hsl(var(--secondary))]"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCurrent ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted-foreground))]/40"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-xs">v{v.version}</span>
                          {isCurrent && <span className="text-[9px] font-bold text-[hsl(var(--primary))] uppercase">Now</span>}
                          {v.created_at && <span className="text-[9px] text-[hsl(var(--muted-foreground))]">{new Date(v.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                        </div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{v.summary}</div>
                      </div>
                      {!isCurrent && (
                        <button onClick={() => revert(v.version)} data-testid={`revert-v${v.version}`}
                          className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] px-2 py-0.5 text-[10px] font-semibold hover:border-[hsl(var(--primary))]/60 hover:text-[hsl(var(--primary))] transition-colors shrink-0">
                          <RotateCcw className="w-3 h-3" /> {label}
                        </button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          <SaveFiles gameId={id} config={config} title={game?.title} refreshKey={savesRefresh} onRestore={(cfg) => setConfig(cfg)} />
        </div>
      </div>
      <ShareExportDialog gameId={id} status={game?.status} open={shareOpen} onClose={() => setShareOpen(false)} />
      <TestPlay config={config} open={testPlayOpen} onClose={() => setTestPlayOpen(false)} />
      <CollaboratorsDialog gameId={id} open={collabOpen} onClose={() => setCollabOpen(false)} isOwner={!game?.shared} />
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Rocket className="w-5 h-5 text-[hsl(var(--primary))]" /> Publish to Arcade</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Description</Label>
              <Textarea value={pubForm.description} onChange={(e) => setPubForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Tell players what your game is about…" data-testid="publish-description" />
            </div>
            <div>
              <Label className="mb-1.5 block">Category</Label>
              <Select value={pubForm.category} onValueChange={(v) => setPubForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="publish-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">All games pass safety moderation. Kid accounts need a parent's approval before going live.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button onClick={publish} data-testid="publish-confirm-btn" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"><Wand2 className="w-4 h-4" /> Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NPC chat dialog */}
      <Dialog open={npcOpen} onOpenChange={setNpcOpen}>
        <DialogContent data-testid="npc-chat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{(config.npcs || [])[npcIdx]?.emoji || "🙂"}</span>
              Chat with {(config.npcs || [])[npcIdx]?.name || "your character"}
            </DialogTitle>
          </DialogHeader>
          <div className="h-72 overflow-y-auto space-y-2 rounded-xl bg-[hsl(var(--secondary))]/30 p-3" data-testid="npc-chat-log">
            {npcMsgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "player" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === "player" ? "bg-[hsl(var(--primary))] text-white rounded-br-sm" : "bg-[hsl(var(--card))] rounded-bl-sm"}`}>
                  {m.text}
                  {m.role === "npc" && (
                    <button onClick={() => speak(m.text, `npc-${i}`, (config.npcs || [])[npcIdx]?.voiceId || npcVoice(npcIdx))} title="Hear voice"
                      className="ml-1.5 opacity-60 hover:opacity-100 align-middle" data-testid={`npc-speak-${i}`}>
                      {speaking === `npc-${i}` ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Volume2 className="w-3 h-3 inline" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {npcSending && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-[hsl(var(--card))] text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</div></div>}
          </div>
          <div className="flex gap-2">
            {npcMicSupported && (
              <Button variant="outline" size="icon" onClick={npcListening ? npcStop : npcStart} className="shrink-0" data-testid="npc-mic-btn" title={npcListening ? "Stop" : "Speak"}>
                {npcListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            )}
            <Input value={npcInput} onChange={(e) => setNpcInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendNpc()}
              placeholder={npcListening ? "Listening…" : "Say something…"} data-testid="npc-chat-input" />
            <Button onClick={sendNpc} disabled={npcSending} size="icon" data-testid="npc-chat-send"><Send className="w-4 h-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
